import * as React from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export interface DialogBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Optional header (e.g. DialogStepHeader). Rendered above scrollable content. */
  header?: React.ReactNode;
}

/** Scrollable body for wizard-style dialogs. Bleeds to dialog edges for full-width content. */
export const DialogBody = React.forwardRef<HTMLDivElement, DialogBodyProps>(
  ({ className, header, children, ...props }, ref) => (
    <div className={cn("flex-1 min-h-0 flex flex-col overflow-hidden", className)} {...props}>
      <div className="flex-1 min-h-0 flex flex-col border rounded-lg bg-muted/40 overflow-hidden">
        {header != null && (
          <div className="px-lg py-md border-b">{header}</div>
        )}
        <div ref={ref} className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-lg py-lg">
          {children}
        </div>
      </div>
    </div>
  )
);
DialogBody.displayName = "DialogBody";

/* ─── Step header: label (left) + stepper circles (right) ─── */

export interface DialogStepHeaderProps {
  current: number;
  total: number;
  labels?: string[];
}

/** Combined step indicator: step label on the left, stepper circles on the right. */
export function DialogStepHeader({ current, total, labels }: DialogStepHeaderProps) {
  if (total <= 0) return null;
  const label = labels?.[current - 1];

  return (
    <div className="flex items-center justify-between gap-md" role="navigation" aria-label="Étapes du formulaire">
      {label && (
        <p className="text-subtitle text-foreground truncate min-w-0" aria-live="polite">
          {label}
        </p>
      )}
      <div className="flex items-center gap-1.5 shrink-0" role="list">
        {Array.from({ length: total }, (_, i) => i + 1).map((step) => {
          const stepLabel = labels?.[step - 1];
          const state = current === step ? 'actuelle' : current > step ? 'complétée' : 'à venir';
          return (
            <React.Fragment key={step}>
              <div
                role="listitem"
                aria-label={`Étape ${step}${stepLabel ? ` : ${stepLabel}` : ''} (${state})`}
                aria-current={current === step ? 'step' : undefined}
                className={cn(
                  "w-6 h-6 rounded-full flex items-center justify-center text-body-sm transition-colors",
                  current === step
                    ? "bg-primary text-primary-foreground"
                    : current > step
                      ? "bg-primary/20 text-primary"
                      : "bg-muted text-muted-foreground"
                )}
              >
                {current > step ? (
                  <Check className="w-3 h-3" aria-hidden />
                ) : (
                  step
                )}
              </div>
              {step < total && (
                <div
                  aria-hidden
                  className={cn(
                    "w-3 h-0.5 transition-colors",
                    current > step ? "bg-primary" : "bg-muted"
                  )}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
DialogStepHeader.displayName = "DialogStepHeader";
